import {useCallback, useMemo} from 'react';
import {DefaultTheme, useTheme} from 'styled-components';
import {Trans, useTranslation} from 'react-i18next';
import {ShoppingCart, UserCheck} from 'lucide-react';
import {useSummary} from 'context/summary';
import {usePropertyProtection} from './usePropertyProtection';
import {PATHS} from 'routes/constants';
import {DEAL_STATUSES, OFFER_CATEGORIES, SUMMARY_PAYMENTS_STATUSES} from '@chekin/core';
import {useGuestGroup} from './useGuestGroup';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useFirstGuestMustBeAdult} from 'hooks/useFirstGuestMustBeAdult';
import {useLayoutMode} from './useLayoutMode';
import {appendLeaderParam} from 'utils/guestForm';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import ChekinProSettings from 'ChekinProSettings';
import {useFetchDeals, useFetchOffers} from 'hooks';

enum NAVIGATION_STATUSES {
  complete = 'COMPLETE',
  incomplete = 'INCOMPLETE',
}

const getBookingPaymentsStatus = (...statuses: Array<string | null | undefined>) =>
  statuses
    .filter(status => !!status && status !== SUMMARY_PAYMENTS_STATUSES.inactive)
    .every(status => status === SUMMARY_PAYMENTS_STATUSES.complete);

const getColor = (theme: DefaultTheme, defaultColor: string, isCompleted?: boolean) => {
  return isCompleted && theme.hasTemplate ? theme.colors.branding.icons : defaultColor;
};

function useBuildNavigationItems() {
  const {t} = useTranslation();
  const theme = useTheme();
  const {data, isSuccess, isInstantCheckInEnabled} = useSummary();
  const {guestGroup} = useGuestGroup();
  const {hasAvantio, data: reservation} = useReservation();
  const layoutMode = useLayoutMode();
  const {data: paginatedPetFeeOffers} = useFetchOffers({
    housingId: reservation?.housing_id,
    category: OFFER_CATEGORIES.petFee,
  });
  const {data: paginatedDealsList} = useFetchDeals({reservationId: reservation?.id});

  const hasPetFeeOffer =
    !ChekinProSettings.isSDK &&
    Boolean(paginatedPetFeeOffers?.count && paginatedPetFeeOffers.count > 0) &&
    Boolean(reservation?.number_of_pets && reservation.number_of_pets > 0);

  const isPetFeeDealPaid =
    hasPetFeeOffer &&
    paginatedDealsList?.results.some(
      deal =>
        deal.offer.category === OFFER_CATEGORIES.petFee &&
        (deal.status === DEAL_STATUSES.paid || deal.status === DEAL_STATUSES.requested),
    );

  const isSecurityDepositMarkedAsPaid = reservation?.security_deposit_marked_as_paid;

  const {
    isMultipleDeposits,
    isSecurityDepositOptional,
    isWaivoProtectionMandatory,
    isInsuranceSetupInit,
    isSecurityDeposit,
    isWaivoProtection,
    insuranceSetup,
  } = usePropertyProtection();
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {firstGuestMustBeAdult: shouldFirstGuestBeAdult} = useFirstGuestMustBeAdult();
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;

  const isSummaryBookingPaymentsCompleted = getBookingPaymentsStatus(
    data?.taxes?.status,
    data?.property_protection?.status,
    data?.booking_payments?.status,
  );
  const isBookingPaymentsCompleted =
    isSummaryBookingPaymentsCompleted && (!hasPetFeeOffer || isPetFeeDealPaid);

  const goToWhoIsRegistering =
    isInstantCheckInEnabled && !guestGroup?.added_number_of_guests;
  const {isIVOnPropertyUnavailable} = useIVOnProperty();

  const getRegistrationPath = useCallback(() => {
    if (layoutMode === 'kiosko') {
      if (hasNoGuests) {
        return `${PATHS.verification.main}/${PATHS.verification.kioskoScanDocument}`;
      } else {
        return PATHS.guestRegistration;
      }
    }
    if (goToWhoIsRegistering) {
      return PATHS.whoIsRegistering;
    }
    if (hasAvantio) {
      return PATHS.guestRegistration;
    }
    if (isIVOnPropertyUnavailable) {
      return PATHS.guestRegistration;
    }
    if (ivEnabled) {
      if (hasNoGuests) {
        return PATHS.verification.main;
      } else {
        return PATHS.guestRegistration;
      }
    } else {
      if (hasNoGuests) {
        const isLeader = ChekinProSettings.isLeader === false ? false : true;
        return appendLeaderParam(PATHS.addPersonalDataForm, isLeader);
      } else {
        return PATHS.guestRegistration;
      }
    }
  }, [
    layoutMode,
    isIVOnPropertyUnavailable,
    ivEnabled,
    hasNoGuests,
    hasAvantio,
    goToWhoIsRegistering,
  ]);

  const getPaymentPath = useCallback(() => {
    if (isBookingPaymentsCompleted) {
      return PATHS.payments.ordersHistory;
    }

    if (insuranceSetup) {
      return PATHS.payments.main;
    }
    const isCurrentSecurityDepositOptional =
      isSecurityDeposit && isSecurityDepositOptional;
    const isWaivoOptional = isWaivoProtection && !isWaivoProtectionMandatory;

    if (
      !isSecurityDepositMarkedAsPaid &&
      isInsuranceSetupInit &&
      (isMultipleDeposits || isCurrentSecurityDepositOptional || isWaivoOptional)
    ) {
      return PATHS.deposits;
    } else {
      return PATHS.payments.main;
    }
  }, [
    isMultipleDeposits,
    isBookingPaymentsCompleted,
    isSecurityDepositOptional,
    isWaivoProtectionMandatory,
    isInsuranceSetupInit,
    isSecurityDeposit,
    isWaivoProtection,
    insuranceSetup,
    isSecurityDepositMarkedAsPaid,
  ]);
  const incompleteIconColor = theme.hasTemplate ? theme.colors.branding.icons : '#777E91';

  const navigationItems = useMemo(() => {
    if (!isSuccess) return [];
    const isCompletedGuestRegistration =
      data?.guest_registration?.status === NAVIGATION_STATUSES.complete;

    const colorGuestRegistrationComplete = getColor(
      theme,
      incompleteIconColor,
      isCompletedGuestRegistration,
    );

    const colorBookingPaymentsComplete = getColor(
      theme,
      incompleteIconColor,
      isBookingPaymentsCompleted,
    );

    return [
      {
        title: 'guest_registration',
        testId: 'guest-registration-nav-item',
        completed: isCompletedGuestRegistration,
        show: /*data?.guest_registration?.editable*/ true,
        path: getRegistrationPath(),
        icon: (
          <UserCheck
            size={20}
            color={colorGuestRegistrationComplete}
            className="kiosko:w-[54px] kiosko:h-[54px]"
          />
        ),
        notification: shouldFirstGuestBeAdult
          ? {
              title: t('first_guest_must_be_an_adult_title'),
              message: <Trans i18nKey="first_guest_must_be_an_adult_message" />,
            }
          : undefined,
      },
      {
        title: 'payments',
        testId: 'booking-payments-nav-item',
        completed: isBookingPaymentsCompleted,
        show:
          [
            data?.property_protection?.active,
            data?.taxes.active,
            data?.booking_payments.active,
            hasPetFeeOffer,
          ].some(Boolean) && layoutMode === 'default',
        path: getPaymentPath(),
        icon: <ShoppingCart size={20} color={colorBookingPaymentsComplete} />,
      },
    ];
  }, [
    data,
    theme,
    incompleteIconColor,
    isSuccess,
    getPaymentPath,
    isBookingPaymentsCompleted,
    hasPetFeeOffer,
    getRegistrationPath,
    shouldFirstGuestBeAdult,
    t,
    layoutMode,
  ]);

  const displayedNavigationItems = useMemo(() => {
    return navigationItems.filter(navItem => navItem.show);
  }, [navigationItems]);

  return {
    navigationItems,
    displayedNavigationItems,
    getPaymentPath,
    isLoading: !isSuccess,
    getRegistrationPath,
  };
}

export {useBuildNavigationItems};
export {NAVIGATION_STATUSES};
